window.ttx = window.ttx || {};
ttx.utils = ttx.utils || {};

// ==================== 按需加载三方包 ====================

/**
 * 动态加载 JS 脚本（内部使用）
 * @param {string} url - 脚本地址
 * @returns {Promise} resolve 时返回加载的脚本
 */
ttx.utils._loadScript = function (url) {
    return new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = function () {
            resolve(script);
        };
        script.onerror = function () {
            reject(new Error('脚本加载失败: ' + url));
        };
        document.head.appendChild(script);
    });
};

/**
 * 按需加载 Excel 工具包（XLSX）
 * @returns {Promise<object>} XLSX 模块
 * @example
 * const XLSX = await ttx.utils.getExcelModule();
 */
ttx.utils.getExcelModule = function () {
    if (window.XLSX) return Promise.resolve(window.XLSX);
    return ttx.utils._loadScript('/static/lib/xlsx.full.min.js').then(function () {
        return window.XLSX;
    });
};

/**
 * 按需加载手机号校验工具（libphonenumber-js）
 * @returns {Promise<object>} PhoneNumber 模块
 * @example
 * const PhoneNumber = await ttx.utils.getPhoneNumberModule();
 */
ttx.utils.getPhoneNumberModule = function () {
    if (window.libphonenumber) return Promise.resolve(window.libphonenumber);
    return ttx.utils._loadScript('/static/lib/libphonenumber-js.min.js').then(function () {
        return window.libphonenumber;
    });
};

/**
 * 按需加载协同文件服务 API
 * @returns {Promise<object>} 协同文件服务 API 对象
 * @example
 * const api = await ttx.utils.getYYCooperationBridge();
 * api.xxx(xxx);
 */
ttx.utils.getYYCooperationBridge = function () {
    if (window.YYCooperationBridge) return Promise.resolve(window.YYCooperationBridge);
    return ttx.utils._loadScript('/static/lib/yy-cooperation-bridge.js').then(function () {
        return window.YYCooperationBridge;
    });
};

/**
 * 按需加载 jQuery 并暴露到全局（window.jQuery / window.$）
 * @returns {Promise<object>} jQuery 实例
 * @example
 * const $ = await ttx.utils.getJQueryModule();
 * $('.my-class').show();
 */
ttx.utils.getJQueryModule = function () {
    if (window.jQuery || window.$) return Promise.resolve(window.jQuery || window.$);
    return ttx.utils._loadScript('/static/lib/jquery.min.js').then(function () {
        var jq = window.jQuery || window.$;
        if (!jq) {
            return Promise.reject(new Error('jQuery 脚本加载完成但未暴露到全局对象'));
        }
        if (!window.jQuery) window.jQuery = jq;
        if (!window.$) window.$ = jq;
        return jq;
    });
};

/**
 * 按需加载协同文件服务组件
 * @returns {Promise<object>} 协同文件服务组件对象
 * @example
 * const YYCooperationBridge = await ttx.utils.getYYFileserviceLib();
 * const Component = YYCooperationBridge[key];
 */
ttx.utils.getYYFileserviceLib = function () {
    if (window.YYFileserviceLib) return Promise.resolve(window.YYFileserviceLib);
    return ttx.utils._loadScript('/static/lib/yy-fileservice-lib.js').then(function () {
        return window.YYFileserviceLib;
    });
};

/**
 * 按需加载 G6 图可视化工具包
 * @param {Function} callback - 加载完成后的回调
 * @example
 * ttx.utils.g6Load(function () {
 *     // 业务代码，此时 window.G6 已可用
 * });
 */
ttx.utils.g6Load = function (callback) {
    if (window.G6) {
        callback && callback();
        return;
    }
    ttx.utils._loadScript('/static/lib/g6.min.js').then(function () {
        callback && callback();
    }).catch(function (err) {
        ttx.console.error('G6 加载失败:', err);
    });
};

/**
 * 按需加载 Echarts 工具包
 * @returns {Promise<object>} Echarts 实例
 * @example
 * const echarts = await ttx.utils.getEchartsModule();
 * const chart = echarts.init(dom);
 */
ttx.utils.getEchartsModule = function () {
    if (window.echarts) return Promise.resolve(window.echarts);
    return ttx.utils._loadScript('/static/lib/echarts.min.js').then(function () {
        return window.echarts;
    });
};

/**
 * 按需加载百度地图 React 组件（react-bmapgl）
 * @returns {Promise<object>} BmapGL 模块
 * @example
 * const BmapComp = await ttx.utils.getBmapModule();
 */
ttx.utils.getBmapModule = function () {
    if (window.BMapGL) return Promise.resolve(window.BMapGL);
    return ttx.utils._loadScript('/static/lib/react-bmapgl.js').then(function () {
        return window.BMapGL;
    });
};

/**
 * 按需加载公式组件（yyuap-formula）
 * @returns {Promise<object>} YYFormula 模块
 * @example
 * const YYFormula = await ttx.utils.getFormulaModule();
 */
ttx.utils.getFormulaModule = function () {
    if (window.YYFormula) return Promise.resolve(window.YYFormula);
    return ttx.utils._loadScript('/static/lib/yyuap-formula.js').then(function () {
        return window.YYFormula;
    });
};

// ==================== require.ensure 模式说明 ====================
// MDF 原生框架使用 require.ensure 按需加载 G6/Echarts/Bmap/Formula 等三方包。
// 本 SDK 使用 ES module（import/export），不支持 require.ensure，因此：
//   1. G6：通过 ttx.events.execute('g6Load', callback) 触发加载（下方注册）
//   2. Echarts：可使用动态 import('echarts')，或上方 getEchartsModule() 兜底
//   3. Bmap：可使用动态 import('react-bmapgl')（建议仅在编辑态加载），或上方 getBmapModule() 兜底
//   4. Formula：可使用动态 import('yyuap-formula')，或上方 getFormulaModule() 兜底
// 动态 import 需宿主工程配置对应依赖；本 SDK 默认通过 _loadScript 注入静态资源兜底。
// =============================================================

// 注册 g6Load 全局事件：业务侧通过 ttx.events.execute('g6Load', callback) 触发 G6 加载
// 多次 execute 只加载一次脚本，后续调用复用 window.G6
if (ttx.events && typeof ttx.events.on === 'function') {
    ttx.events.on('g6Load', function (callback) {
        ttx.utils.g6Load(callback);
    });
}
